import { useState } from "react";
import { Card } from "@/src/components/wrappers/Card";
import { Switch } from "@/src/components/ui/switch";
import { Button } from "@/src/components/ui/button";
import { Input } from "@/src/components/ui/input";
import { useQueryClient } from "@tanstack/react-query";
import { CheckIcon, PencilIcon, Trash2Icon } from "lucide-react";
import { cn } from "@/src/lib/utils";
import { Booking, BookingAddendumRecord, BookingContractStatus, BookingFile } from "../../model";
import { UploadInvoiceButton } from "../UploadInvoiceButton";
import { useBookingContractSignedMutation } from "@/src/lib/bookingContractSignedMutation";
import {
  useBookingContractStatusMutation,
  useCreateBookingAddendumMutation,
  useDeleteBookingAddendumMutation,
  useUpdateBookingAddendumMutation,
} from "@/src/lib/bookingContractMutations";

const STAGES: { key: Exclude<BookingContractStatus, "none">; label: string }[] = [
  { key: "sent", label: "Sent to client" },
  { key: "opened", label: "Client opened" },
  { key: "client_signed", label: "Client signed" },
  { key: "countersigned", label: "HELM countersigned" },
];
const STAGE_ORDER: BookingContractStatus[] = ["none", "sent", "opened", "client_signed", "countersigned"];

const fmtDate = (iso?: string | null) =>
  iso ? new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" }) : null;

const FileRow = ({ file }: { file: BookingFile }) => (
  <div className="flex items-center justify-between gap-3 rounded-lg border border-gray-200 px-4 py-2.5">
    <span className="flex min-w-0 items-center gap-2">
      <span aria-hidden>📄</span>
      <span className="truncate text-sm">{file.name}</span>
    </span>
    <a
      href={file.url}
      target="_blank"
      rel="noopener noreferrer"
      className="shrink-0 text-sm font-medium text-blue-600 hover:underline"
    >
      View
    </a>
  </div>
);

const StatusBadge = ({ status, template }: { status: BookingContractStatus; template: string }) => {
  const cls =
    status === "countersigned"
      ? "bg-green-100 text-green-800"
      : status === "client_signed"
        ? "bg-amber-100 text-amber-800"
        : status === "none"
          ? "bg-gray-100 text-gray-600"
          : "bg-blue-100 text-blue-800";
  const label =
    status === "countersigned"
      ? "Signed & countersigned"
      : status === "client_signed"
        ? "Awaiting HELM countersignature"
        : status === "none"
          ? "Draft"
          : "Out for signature";
  return <span className={cn("rounded-full px-3 py-1 text-xs font-medium", cls)}>{`${template} · ${label}`}</span>;
};

/** Signature progress strip. v1 signs outside the app — clicking a step sets the
 *  booking to that stage (forwards or back); the server stamps first arrivals and
 *  drops stamps beyond a walk-back. */
const SignatureStrip = ({ booking }: { booking: Booking }) => {
  const statusMutation = useBookingContractStatusMutation(booking.id);
  const status: BookingContractStatus = booking.contractStatus ?? (booking.contractSigned ? "client_signed" : "none");
  const currentIndex = STAGE_ORDER.indexOf(status);
  const nextStage = STAGE_ORDER[currentIndex + 1] as Exclude<BookingContractStatus, "none"> | undefined;

  return (
    <div className="flex flex-col gap-3 rounded-lg border border-gray-200 p-4">
      <div className="flex items-center gap-2 text-xs text-gray-500">
        <span className="rounded-full bg-gray-100 px-2 py-0.5 font-medium text-gray-600">Signed outside the app</span>
        <span>the client signs the paper copy; mark each stage here as it happens</span>
      </div>
      <div className="flex flex-wrap gap-2">
        {STAGES.map((stage, i) => {
          const done = currentIndex >= i + 1;
          const date = fmtDate(booking.contractStatusDates?.[stage.key]);
          return (
            <button
              key={stage.key}
              type="button"
              disabled={statusMutation.isPending}
              title={done && i + 1 < currentIndex ? `Walk back to “${stage.label}”` : undefined}
              onClick={() => statusMutation.mutate(done && i + 1 === currentIndex ? STAGE_ORDER[i] : stage.key)}
              className={cn(
                "flex min-w-[130px] flex-1 flex-col items-start gap-0.5 rounded-md border px-3 py-2 text-left text-sm transition-colors",
                done ? "border-green-300 bg-green-50 text-green-900" : "border-gray-200 text-gray-500 hover:bg-gray-50",
              )}
            >
              <span className="flex items-center gap-1.5 font-medium">
                {done && <CheckIcon size={14} />}
                {stage.label}
              </span>
              <span className="text-xs text-gray-400">{date ?? "—"}</span>
            </button>
          );
        })}
      </div>
      <div className="flex items-center gap-2">
        {nextStage ? (
          <Button size="sm" disabled={statusMutation.isPending} onClick={() => statusMutation.mutate(nextStage)}>
            <CheckIcon size={14} className="mr-1" />
            Mark {STAGES.find((s) => s.key === nextStage)?.label.toLowerCase()}
          </Button>
        ) : (
          <span className="text-sm text-green-700">
            Fully executed{booking.contractStatusDates?.countersigned ? ` · ${fmtDate(booking.contractStatusDates.countersigned)}` : ""}
          </span>
        )}
        <span className="text-xs text-gray-400">Click a completed step to walk the stage back.</span>
      </div>
    </div>
  );
};

const ADDENDUM_STATE_BADGE: Record<BookingAddendumRecord["state"], { cls: string; label: string }> = {
  draft: { cls: "bg-gray-100 text-gray-600", label: "Draft" },
  sent: { cls: "bg-amber-100 text-amber-800", label: "Out for signature" },
  signed: { cls: "bg-green-100 text-green-800", label: "Signed" },
};

const AddendumRow = ({ booking, addendum }: { booking: Booking; addendum: BookingAddendumRecord }) => {
  const updateMutation = useUpdateBookingAddendumMutation(booking.id);
  const deleteMutation = useDeleteBookingAddendumMutation(booking.id);
  const [editing, setEditing] = useState(false);
  const [description, setDescription] = useState(addendum.description ?? "");
  const [effect, setEffect] = useState(addendum.effect ?? "");
  const busy = updateMutation.isPending || deleteMutation.isPending;
  const badge = ADDENDUM_STATE_BADGE[addendum.state];

  return (
    <div className="flex flex-col gap-2 rounded-lg border border-gray-200 px-4 py-3">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-sm font-semibold">Addendum {addendum.number}</span>
        {!editing && addendum.description && <span className="text-sm text-gray-600">· {addendum.description}</span>}
        <span className={cn("rounded-full px-2.5 py-0.5 text-xs font-medium", badge.cls)}>
          {badge.label}
          {addendum.state === "signed" && addendum.signedDate ? ` · ${fmtDate(addendum.signedDate)}` : ""}
        </span>
        <span className="ml-auto flex items-center gap-1.5">
          {addendum.state === "draft" && (
            <Button size="sm" variant="outline" disabled={busy} onClick={() => updateMutation.mutate({ addendumId: addendum.id, state: "sent" })}>
              Mark sent
            </Button>
          )}
          {addendum.state !== "signed" && (
            <Button size="sm" disabled={busy} onClick={() => updateMutation.mutate({ addendumId: addendum.id, state: "signed" })}>
              Mark signed
            </Button>
          )}
          {addendum.state === "signed" && (
            <Button size="sm" variant="outline" disabled={busy} onClick={() => updateMutation.mutate({ addendumId: addendum.id, state: "sent" })}>
              Unsign
            </Button>
          )}
          {addendum.file && (
            <a
              href={addendum.file.url}
              target="_blank"
              rel="noopener noreferrer"
              className="text-sm font-medium text-blue-600 hover:underline"
            >
              View
            </a>
          )}
          <button
            type="button"
            aria-label={`Edit addendum ${addendum.number}`}
            className="rounded p-1 text-gray-400 hover:text-gray-700"
            onClick={() => setEditing((v) => !v)}
          >
            <PencilIcon size={14} />
          </button>
          <button
            type="button"
            aria-label={`Delete addendum ${addendum.number}`}
            className="rounded p-1 text-gray-400 hover:text-red-600"
            disabled={busy}
            onClick={() => {
              if (window.confirm(`Remove addendum ${addendum.number}? The uploaded document stays on the booking.`)) {
                deleteMutation.mutate(addendum.id);
              }
            }}
          >
            <Trash2Icon size={14} />
          </button>
        </span>
      </div>
      {!editing && addendum.effect && <p className="text-xs text-gray-500">{addendum.effect}</p>}
      {editing && (
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
          <Input
            value={description}
            placeholder="What it covers (e.g. Dates moved to 5–19 Aug)"
            onChange={(e) => setDescription(e.target.value)}
          />
          <Input value={effect} placeholder="Effect (e.g. fee +€4,000)" onChange={(e) => setEffect(e.target.value)} />
          <Button
            size="sm"
            disabled={busy}
            onClick={() => {
              updateMutation.mutate({ addendumId: addendum.id, description, effect });
              setEditing(false);
            }}
          >
            Save
          </Button>
        </div>
      )}
    </div>
  );
};

const ContractSection = ({ booking }: { booking: Booking }) => {
  const queryClient = useQueryClient();
  const signMutation = useBookingContractSignedMutation(booking.id);
  const createMutation = useCreateBookingAddendumMutation(booking.id);
  const refetch = () => queryClient.invalidateQueries({ queryKey: ["bookingYachtOfferData", booking.id] });

  const contract = booking.contractFile;
  const records = booking.addendumRecords ?? [];
  const linkedFileIds = new Set(records.map((r) => r.file?.id).filter(Boolean));
  const unlinkedFiles = (booking.addendums ?? []).filter((f) => !linkedFileIds.has(f.id));
  const template = (booking.contractTemplate || "Charter").toUpperCase();
  const status: BookingContractStatus = booking.contractStatus ?? (booking.contractSigned ? "client_signed" : "none");
  const signedCount = records.filter((r) => r.state === "signed").length;

  return (
    <div className="flex w-full flex-col gap-6">
      <Card className="flex flex-col gap-6">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <span className="flex items-center gap-3">
            <h3 className="text-lg font-semibold">{template} charter agreement</h3>
            <StatusBadge status={status} template={template} />
          </span>
          <label className="flex items-center gap-2">
            <span className="text-sm text-gray-600">Contract signed</span>
            <Switch
              checked={!!booking.contractSigned}
              disabled={signMutation.isPending}
              onCheckedChange={(v) => signMutation.mutate(!!v)}
            />
          </label>
        </div>

        <SignatureStrip booking={booking} />

        <div className="flex flex-col gap-2">
          {contract ? <FileRow file={contract} /> : <p className="text-sm text-gray-500">No contract on file yet.</p>}
          <div>
            <UploadInvoiceButton ownerType="contract" ownerId={booking.id} onUploaded={refetch}>
              {contract ? "Replace contract" : "Upload contract"}
            </UploadInvoiceButton>
          </div>
        </div>
      </Card>

      <Card className="flex flex-col gap-4">
        <div className="flex flex-wrap items-center gap-3">
          <h3 className="text-lg font-semibold">Addendums</h3>
          <span
            className={cn(
              "rounded-full px-2.5 py-0.5 text-xs font-medium",
              records.length && signedCount < records.length ? "bg-amber-100 text-amber-800" : "bg-gray-100 text-gray-600",
            )}
          >
            {signedCount} of {records.length} signed
          </span>
          <span className="text-xs text-gray-400">amend a signed contract without re-issuing it</span>
          <span className="ml-auto flex items-center gap-2">
            <Button size="sm" variant="outline" disabled={createMutation.isPending} onClick={() => createMutation.mutate({})}>
              New addendum
            </Button>
            <UploadInvoiceButton
              ownerType="addendum"
              ownerId={booking.id}
              onUploaded={(file) => createMutation.mutate({ fileId: file.id })}
            >
              Upload addendum
            </UploadInvoiceButton>
          </span>
        </div>

        {records.length > 0 ? (
          <div className="flex flex-col gap-2">
            {records.map((a) => (
              <AddendumRow key={a.id} booking={booking} addendum={a} />
            ))}
          </div>
        ) : (
          <p className="text-sm text-gray-500">
            No addendums. Raise one for any change to dates, ports, guests, APA or fee after the contract is signed.
          </p>
        )}

        {unlinkedFiles.length > 0 && (
          <div className="flex flex-col gap-2">
            <span className="text-xs font-medium uppercase tracking-wide text-gray-400">
              Documents without an addendum record
            </span>
            {unlinkedFiles.map((f) => (
              <div key={f.id} className="flex items-center gap-3">
                <div className="grow">
                  <FileRow file={f} />
                </div>
                <Button
                  size="sm"
                  variant="outline"
                  disabled={createMutation.isPending}
                  onClick={() => createMutation.mutate({ fileId: f.id })}
                >
                  Create record
                </Button>
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
};

export default ContractSection;
